/**
 * Das Titelbild einer Reise — die Wahl, nicht das Bild.
 *
 * ## Zwei Wege zu einem Bild
 *
 * Eine Reise bekommt ihr Titelbild von allein: Der Server sucht in
 * `GET /trips` das jüngste Foto eines **geteilten** Tagebucheintrags und hängt
 * es als `titelbild_eintrag`/`titelbild_medium` an die Reise. Das ist die
 * Automatik, und sie ist der Normalfall — niemand soll ein Bild aussuchen
 * müssen, damit die Liste nicht nackt aussieht.
 *
 * Wem die Automatik das falsche Bild wählt, der übersteuert sie: Im Tagebuch
 * steht an jedem Foto `Als Titelbild`, und das schreibt
 * `settings.titelbild = { eintrag, medium }`. Den Vorrang rechnet **der
 * Server** — er liefert in den beiden Feldern schon das Ergebnis. Diese Datei
 * rechnet ihn ausdrücklich nicht nach: Zwei Fassungen derselben Vorrangregel
 * wären zwei Gelegenheiten, sie verschieden zu treffen, und beide sähen für
 * sich richtig aus.
 *
 * ## Warum eine eigene Datei
 *
 * `PATCH /trips/{id}` ersetzt `settings` als Ganzes (`db.update_trip`). Wer
 * nur `{ titelbild: … }` schickt, löscht damit die Straßenvorgaben, die
 * abgehakten Vignetten und alles andere aus dem Block — lautlos, und man merkt
 * es erst, wenn die Route plötzlich über die Autobahn läuft. Dieselbe Falle
 * wie bei den Vignetten, also dieselbe Antwort: eine reine Funktion, die den
 * **vollen** Block mit ersetztem Schlüssel zurückgibt (`mitVignetten` in
 * `kosten.ts` ist die Vorlage), und Tests, die ohne Browser laufen.
 *
 * **Reine Funktionen** — kein DOM, kein Netz, keine Uhr.
 */

// Wem die Reise gehört, beantwortet `mitplanen` und nicht diese Datei — eine
// zweite Auslegung von „fremd" wäre eine zweite Gelegenheit, sie verschieden zu
// treffen. Gebraucht wird sie für das gezeichnete Titelbild: Dessen Route ist
// besitzergebunden.
import { istFremd, type Reisebeteiligung } from './mitplanen';

/** Der Schlüssel im freien Einstellungsblock der Reise. */
export const TITELBILD = 'titelbild';

/** Ein ausgesuchtes Foto: der Tagebucheintrag und das Medium darin. */
export interface Titelbildwahl {
  eintrag: number;
  medium: number;
}

/** Der freie Bereich einer Reise, soweit diese Datei ihn braucht. */
type Einstellungen = Record<string, unknown> | null | undefined;

/**
 * Eine Kennung aus fremder Hand — oder `null`.
 *
 * `settings` kommt vom Server und ist frei; dort kann alles stehen. Was keine
 * brauchbare Kennung ist (Text, Komma, Null, negativ), fällt heraus, statt als
 * `NaN` in einen Vergleich zu laufen und dort ein Foto zum Titelbild zu
 * erklären, das niemand angeklickt hat. Dieselbe Vorsicht wie in
 * `kosten.vignettenAus`.
 */
function kennung(roh: unknown): number | null {
  return typeof roh === 'number' && Number.isInteger(roh) && roh > 0 ? roh : null;
}

/**
 * Das ausdrücklich gewählte Titelbild — `null` heißt: Automatik.
 *
 * Fehlt eine der beiden Zahlen oder taugt sie nichts, gilt die ganze Wahl als
 * nicht getroffen. Ein halbes Paar wäre schlimmer als keines: Es zeigte auf
 * einen Eintrag ohne Medium, und der Server antwortete beim Laden mit 404.
 */
export function titelbildAus(settings: Einstellungen): Titelbildwahl | null {
  const roh = (settings ?? {})[TITELBILD];
  if (!roh || typeof roh !== 'object') return null;
  const eintrag = kennung((roh as Record<string, unknown>).eintrag);
  const medium = kennung((roh as Record<string, unknown>).medium);
  return eintrag && medium ? { eintrag, medium } : null;
}

/**
 * Ist **dieses** Foto das ausdrücklich gewählte Titelbild?
 *
 * Die Frage stellt das Tagebuch an jedem Foto, um zwischen `Als Titelbild` und
 * `Automatik wiederherstellen` zu unterscheiden. Sie hängt ausdrücklich an der
 * **Wahl** und nicht an dem Bild, das die Reiseliste gerade zeigt: Bei
 * laufender Automatik ist eines von beiden zufällig dasselbe, und ein Knopf,
 * der dann `Automatik wiederherstellen` hieße, böte an, etwas zurückzunehmen,
 * das nie geschehen ist.
 */
export function istGewaehlt(
  wahl: Titelbildwahl | null,
  eintrag: number,
  medium: number
): boolean {
  return wahl != null && wahl.eintrag === eintrag && wahl.medium === medium;
}

/**
 * Die Wahl setzen — und den **ganzen** Einstellungsblock zurückgeben.
 *
 * `null` löscht den Schlüssel und stellt damit die Automatik wieder her. Ein
 * `titelbild: null` im Block täte dasselbe (der Server liest beides als „nicht
 * gesetzt"), wäre aber ein Eintrag, der behauptet, jemand habe sich für nichts
 * entschieden. Weg ist weg.
 *
 * Eine unbrauchbare Wahl löscht ebenfalls, statt sie hinauszuschicken: Der
 * Server weist sie mit 422 ab, und die Fehlermeldung stünde dann an einer
 * Stelle, an der jemand nur ein Foto angeklickt hat. Die Automatik ist der
 * richtige Rückfall — sie hat immer eine Antwort.
 */
export function mitTitelbild(
  settings: Einstellungen,
  wahl: Titelbildwahl | null
): Record<string, unknown> {
  const raus = { ...(settings ?? {}) };
  const eintrag = kennung(wahl?.eintrag);
  const medium = kennung(wahl?.medium);
  if (eintrag && medium) raus[TITELBILD] = { eintrag, medium };
  else delete raus[TITELBILD];
  return raus;
}

/* ══ Das Bild eines Kapitels ═════════════════════════════════════════════
 *
 * Dieselbe Sache eine Ebene tiefer: Auf der geteilten Seite trägt nicht nur die
 * Reise ein Bild, sondern jede Gegend („Toskana"). Bis zum 20.9.2026 rechnete
 * das ausschließlich der Browser (`kapitel.kapitelfoto`: das erste Foto des
 * jüngsten geteilten Eintrags der Gegend), und wem die Automatik daneben griff,
 * der hatte keinen Griff.
 *
 * ## Warum der Kapitelname der Schlüssel ist
 *
 * Nicht die Station: Ein Kapitel ist eine Marke an einer Station
 * (`stops.kapitel`), und `PUT /trips/{id}/stops` ersetzt die Liste per
 * DELETE + INSERT — jede Kennung wechselt dabei. Eine Wahl an `stops.id` wäre
 * nach dem ersten Umsortieren ein Verweis ins Leere. Der Name überlebt beides,
 * genau wie beim Einklappzustand (`kapitelklappen.MITLESE_SCHLUESSEL`).
 *
 * Zwei Grenzen folgen daraus, und beide stehen so auch in der Hilfe:
 *
 * 1. **Zwei gleichnamige Kapitel teilen sich die Wahl.** Dieselbe Unschärfe wie
 *    bei den Klappen, und die harmlose Richtung des Fehlers: Es verschwindet
 *    nichts, es taucht höchstens dasselbe Bild zweimal auf.
 * 2. **Ein umbenanntes Kapitel fällt auf die Automatik zurück.** Die alte Wahl
 *    bleibt als toter Schlüssel liegen und stört nicht — beim nächsten
 *    Schreiben räumt `mitKapitelbild` sie ab, sobald es die Namen der Reise
 *    kennt.
 */

/** Der Schlüssel im freien Einstellungsblock der Reise. */
export const KAPITELBILDER = 'kapitelbilder';

/** Der ganze Block: Kapitelname → Wahl. Leere Objekte gibt es nicht. */
export type Kapitelbilder = Record<string, Titelbildwahl>;

/** Der Block aus `settings`, roh gelesen — ohne die kaputten Einträge. */
function kapitelbilderAus(settings: Einstellungen): Kapitelbilder {
  const roh = (settings ?? {})[KAPITELBILDER];
  if (!roh || typeof roh !== 'object' || Array.isArray(roh)) return {};
  const raus: Kapitelbilder = {};
  for (const [name, wert] of Object.entries(roh as Record<string, unknown>)) {
    // Derselbe Maßstab wie bei `titelbildAus`: Was keine brauchbare Kennung
    // ist, gilt als keine Wahl. Ein halbes Paar wäre schlimmer als keines.
    if (!name.trim() || !wert || typeof wert !== 'object') continue;
    const eintrag = kennung((wert as Record<string, unknown>).eintrag);
    const medium = kennung((wert as Record<string, unknown>).medium);
    if (eintrag && medium) raus[name.trim()] = { eintrag, medium };
  }
  return raus;
}

/**
 * Die ausdrückliche Wahl für **dieses** Kapitel — `null` heißt: Automatik.
 *
 * Der Name wird getrimmt verglichen, weil er getrimmt geschrieben wird
 * (`kapitel.kapitelMarke` liefert ihn so). Ein Kapitel ohne Namen (die Anreise
 * vor der ersten Marke) hat keine Wahl: Es ist kein Kapitel, es hat keinen
 * Kopf, und ein Bild darüber wäre eines über nichts.
 */
export function kapitelbildwahl(
  settings: Einstellungen,
  name: string | null | undefined
): Titelbildwahl | null {
  const gesucht = (name ?? '').trim();
  if (!gesucht) return null;
  return kapitelbilderAus(settings)[gesucht] ?? null;
}

/**
 * Die Wahl eines Kapitels setzen — und den **ganzen** Einstellungsblock
 * zurückgeben.
 *
 * Derselbe Grund wie bei `mitTitelbild`: `PATCH /trips/{id}` ersetzt `settings`
 * als Ganzes, und wer nur seinen einen Schlüssel schickt, löscht lautlos die
 * Straßenvorgaben, die abgehakten Vignetten und das Titelbild gleich mit.
 *
 * `wahl = null` löscht den Schlüssel; bleibt danach kein Kapitel übrig,
 * verschwindet der ganze Block statt als leeres Objekt stehenzubleiben. Ein
 * `kapitelbilder: {}` wäre die Behauptung, jemand habe sich für nichts
 * entschieden — dieselbe Überlegung wie beim Titelbild.
 *
 * `namenJetzt` sind die Kapitel, die es in dieser Reise **heute** gibt. Sind
 * sie bekannt, fallen Schlüssel ohne Kapitel dabei heraus: Wer ein Kapitel
 * umbenennt, lässt eine Wahl zurück, die nie wieder greift, und sie zählt
 * trotzdem gegen die Obergrenze des Servers. Ohne die Angabe wird nichts
 * abgeräumt — eine Fläche, die die Stationen gar nicht kennt (das Tagebuch),
 * darf nicht aus ihrem Nichtwissen auf ein fehlendes Kapitel schließen.
 */
export function mitKapitelbild(
  settings: Einstellungen,
  name: string,
  wahl: Titelbildwahl | null,
  namenJetzt?: readonly (string | null)[] | null
): Record<string, unknown> {
  const raus = { ...(settings ?? {}) };
  const block = kapitelbilderAus(settings);
  const schluessel = (name ?? '').trim();

  if (namenJetzt) {
    const lebend = new Set(namenJetzt.map((n) => (n ?? '').trim()).filter(Boolean));
    // Der gerade gesetzte Name bleibt in jedem Fall stehen: Er kommt vom
    // Kapitelkopf, den der Mensch vor sich hat, und eine Liste, die ihn nicht
    // enthält, ist die veraltete von beiden.
    for (const vorhanden of Object.keys(block)) {
      if (vorhanden !== schluessel && !lebend.has(vorhanden)) delete block[vorhanden];
    }
  }

  const eintrag = kennung(wahl?.eintrag);
  const medium = kennung(wahl?.medium);
  // Eine unbrauchbare Wahl löscht, statt hinauszugehen — der Server wiese sie
  // mit 422 ab, und der Satz stünde dann an einer Stelle, an der jemand nur ein
  // Foto angeklickt hat. Die Automatik ist der richtige Rückfall.
  if (schluessel && eintrag && medium) block[schluessel] = { eintrag, medium };
  else if (schluessel) delete block[schluessel];

  if (Object.keys(block).length) raus[KAPITELBILDER] = block;
  else delete raus[KAPITELBILDER];
  return raus;
}

/**
 * Das Titelbild, das die Reiseliste laden soll — aus der Antwort des Servers.
 *
 * Beide Felder sind optional, weil ein älterer Server sie nicht mitschickt;
 * dann gibt es kein Titelbild, und die Zeile sieht aus wie vorher. Dass hier
 * **nicht** auf `settings` zurückgegriffen wird, ist Absicht — siehe der
 * Kopfkommentar: Den Vorrang rechnet der Server.
 *
 * Ob das Medium wirklich noch da ist, prüft niemand: Der Server sieht bei der
 * Auskunft nicht nach, und ein gelöschtes Foto ergibt beim Laden einen 404.
 * Der behandelt sich still — eine Reise ohne Titelbild ist kein Fehlerfall.
 */
export function titelbildDerReise(reise: {
  titelbild_eintrag?: number | null;
  titelbild_medium?: number | null;
}): Titelbildwahl | null {
  const eintrag = kennung(reise.titelbild_eintrag);
  const medium = kennung(reise.titelbild_medium);
  return eintrag && medium ? { eintrag, medium } : null;
}

/**
 * Fragt diese Reise nach einem **gezeichneten** Titelbild?
 *
 * ## Die Vorrangregel entsteht hier
 *
 * `GET /trips/{id}/titelbild-ki.svg` liefert ein bewusst abstraktes, gezeichnetes
 * Bild und fragt nicht nach, ob die Reise auch ein echtes Foto hätte. Die
 * Reihenfolge **Wahl > gefundenes Foto > gezeichnetes Bild** entsteht deshalb
 * genau an dieser Stelle und nirgendwo sonst: Der Server prüft sie nicht nach,
 * und wer die Route ohne diese Frage abriefe, ersetzte an jeder Reise mit
 * Fotos das Foto durch eine Zeichnung.
 *
 * Die ersten beiden Stufen beantwortet `titelbildDerReise` in einem: Der
 * Server hat den Vorrang zwischen ausdrücklicher Wahl und Automatik schon
 * gerechnet (siehe Kopfkommentar). Bleibt dort nichts übrig, hat diese Reise
 * kein Foto, das die Liste zeigen könnte — erst dann wird gezeichnet.
 *
 * ## Nur an eigenen Reisen
 *
 * Die Route ist besitzergebunden. An einer Reise, an der man bloß mitplant,
 * wäre der Abruf ein 404 je Zeile — Anfragen, deren Antwort schon vor dem
 * Absenden feststeht. `istFremd` beantwortet die Frage, und zwar dieselbe
 * Fassung wie am Reisebuch-Knopf.
 *
 * `vorhandene` ist das Gedächtnis der Fläche (`Map` oder `Set` über die
 * Reisekennungen): Was schon ein Bild trägt, fragt nicht noch einmal.
 */
export function brauchtGezeichnetes(
  reise: {
    id: number;
    titelbild_eintrag?: number | null;
    titelbild_medium?: number | null;
  } & Reisebeteiligung,
  vorhandene: { has(id: number): boolean } | null | undefined
): boolean {
  if (vorhandene?.has(reise.id)) return false;
  if (istFremd(reise)) return false;
  return titelbildDerReise(reise) == null;
}
